import { Col, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import { 
  FiUsers, 
  FiUserCheck, 
  FiCalendar, 
  FiFolder, 
  FiAlertTriangle, 
  FiPackage 
} from "react-icons/fi";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { fetchKPIData, fetchWeeklyAttendance, DashboardKPIData, WeeklyAttendanceData } from "../services/DashboardDataService";

const KPICards = () => {
  const [kpiData, setKpiData] = useState<DashboardKPIData | null>(null);
  const [weeklyAttendance, setWeeklyAttendance] = useState<WeeklyAttendanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [kpiResult, weeklyResult] = await Promise.all([
          fetchKPIData(),
          fetchWeeklyAttendance(),
        ]);
        
        setKpiData(kpiResult);
        setWeeklyAttendance(weeklyResult);
      } catch (err) {
        console.error('Error loading KPI data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const createSparklineData = (weeklyData: WeeklyAttendanceData | null, index: number) => {
    if (!weeklyData) {
      return Array.from({ length: 7 }, (_, i) => ({
        name: ['M', 'T', 'W', 'T', 'F', 'S', 'S'][i],
        value: Math.floor(Math.random() * 50) + 50,
      }));
    }
    
    // Use different data points for each card based on index
    const data = weeklyData.thisWeek.map((value, i) => ({
      name: weeklyData.labels[i],
      value: Math.floor(value * (0.8 + Math.random() * 0.4)), // Add some variation
    }));
    
    return data;
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  const getKPIConfig = (index: number) => {
    // const configs = [
    //   {
    //     title: "Total Employees",
    //     value: kpiData?.totalEmployees || 0,
    //     trend: "+0%",
    //     trendDirection: "positive" as const,
    //     icon: <FiUsers />,
    //     colorClass: "purple",
    //     iconColor: "purple",
    //     color: "rgb(168, 85, 247)",
    //   },
    //   {
    //     title: "Present Today",
    //     value: kpiData?.presentToday || 0,
    //     trend: "+0%",
    //     trendDirection: "positive" as const,
    //     icon: <FiUserCheck />,
    //     colorClass: "blue",
    //     iconColor: "blue",
    //     color: "rgb(96, 165, 250)",
    //   },
    //   {
    //     title: "Employees on Leave",
    //     value: kpiData?.employeesOnLeave || 0,
    //     trend: "-0%",
    //     trendDirection: "negative" as const,
    //     icon: <FiCalendar />,
    //     colorClass: "green",
    //     iconColor: "green",
    //     color: "rgb(52, 211, 153)",
    //   },
    //   {
    //     title: "Active Projects",
    //     value: kpiData?.activeProjects || 0,
    //     trend: "+0%",
    //     trendDirection: "positive" as const,
    //     icon: <FiFolder />,
    //     colorClass: "orange",
    //     iconColor: "orange",
    //     color: "rgb(251, 191, 36)",
    //   },
    //   {
    //     title: "Open Incidents",
    //     value: kpiData?.openIncidents || 0,
    //     trend: "-0%",
    //     trendDirection: "positive" as const,
    //     icon: <FiAlertTriangle />,
    //     colorClass: "red",
    //     iconColor: "red",
    //     color: "rgb(248, 113, 113)",
    //   },
    //   {
    //     title: "Total Assets",
    //     value: kpiData?.totalAssets || 0,
    //     trend: "+0%",
    //     trendDirection: "positive" as const,
    //     icon: <FiPackage />,
    //     colorClass: "pink",
    //     iconColor: "pink",
    //     color: "rgb(244, 114, 182)",
    //   },
    // ];
    const configs = [
    {
      title: "Total Employees",
      value: kpiData?.totalEmployees || 0,
      trend: "+0%",
      trendDirection: "positive" as const,
      icon: <FiUsers />,
      colorClass: "purple",
      iconColor: "purple",
      color: "rgb(168, 85, 247)",
      comingSoon: false,
    },
    {
      title: "Present Today",
      value: kpiData?.presentToday || 0,
      trend: "+0%",
      trendDirection: "positive" as const,
      icon: <FiUserCheck />,
      colorClass: "blue",
      iconColor: "blue",
      color: "rgb(96, 165, 250)",
      comingSoon: false,
    },
    {
      title: "Employees on Leave",
      value: kpiData?.employeesOnLeave || 0,
      trend: "-0%",
      trendDirection: "negative" as const,
      icon: <FiCalendar />,
      colorClass: "green",
      iconColor: "green",
      color: "rgb(52, 211, 153)",
      comingSoon: true,
    },
    {
      title: "Active Projects",
      value: kpiData?.activeProjects || 0,
      trend: "+0%",
      trendDirection: "positive" as const,
      icon: <FiFolder />,
      colorClass: "orange",
      iconColor: "orange",
      color: "rgb(251, 191, 36)",
      comingSoon: false,
    },
    {
      title: "Open Incidents",
      value: kpiData?.openIncidents || 0,
      trend: "-0%",
      trendDirection: "positive" as const,
      icon: <FiAlertTriangle />,
      colorClass: "red",
      iconColor: "red",
      color: "rgb(248, 113, 113)",
      comingSoon: false,
    },
    {
      title: "Total Assets",
      value: kpiData?.totalAssets || 0,
      trend: "+0%",
      trendDirection: "positive" as const,
      icon: <FiPackage />,
      colorClass: "pink",
      iconColor: "pink",
      color: "rgb(244, 114, 182)",
      comingSoon: false,
    },
  ];
    return configs[index];
  };

  if (loading) {
    return (
      <Row className="g-4">
        {[...Array(6)].map((_, index) => (
          // <Col key={index} xl={2} lg={4} md={6} sm={12}>
          <Col key={index} xxl={2} xl={2} lg={3} md={4} sm={6}>
            <div className="glass-card kpi-card">
              <div className="kpi-value">...</div>
              <div className="kpi-label">Loading...</div>
            </div>
          </Col>
        ))}
      </Row>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-4 text-center text-danger">
        {error}
      </div>
    );
  }

  return (
    <Row className="g-2">
      {[...Array(6)].map((_, index) => {
        const kpi = getKPIConfig(index);
        return (
          <Col key={index} xl={2} lg={4} md={6} sm={12}>
            {/* <div className={`glass-card kpi-card ${kpi.colorClass}`}>
              <div className={`kpi-icon ${kpi.iconColor}`}>
                {kpi.icon}
              </div>
              <div className="kpi-value">{formatNumber(kpi.value)}</div>
              <div className="kpi-label">{kpi.title}</div>
              <div className={`kpi-trend ${kpi.trendDirection}`}>
                {kpi.trendDirection === "positive" ? "↑" : "↓"} {kpi.trend}
              </div>
              <div style={{ height: 40, marginTop: '0.5rem' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={createSparklineData(weeklyAttendance, index)}>
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={kpi.color}
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div> */}
            <div className={`glass-card kpi-card ${kpi.colorClass}`}>
              <div className="kpi-header">
                <div className="kpi-left">
                  <div className={`kpi-icon ${kpi.iconColor}`}>
                    {kpi.icon}
                  </div>

                  <div className={`kpi-trend ${kpi.trendDirection}`}>
                    {kpi.trendDirection === "positive" ? "↑" : "↓"} {kpi.trend}
                  </div>
                </div>

                <div className="kpi-right">
                  {/* <div className="kpi-value">{formatNumber(kpi.value)}</div> */}
                  <div className="kpi-value">
                    {kpi.comingSoon ? "Coming Soon" : formatNumber(kpi.value)}
                  </div>
                  <div className="kpi-label">{kpi.title}</div>
                </div>
              </div>
            </div>
          </Col>
        );
      })}
    </Row>
  );
};

export default KPICards;
